import type { CatalogRow, DatasetFacts } from '@/shared/api/generated/catalog';

/* Which datasets a bulk action still has work for, and how far it has got.
 *
 * **Pure, and separated from the runner for one reason: the decisions here are the expensive ones.**
 * Whether a dataset is skipped decides whether 20 full `GROUP BY` scans run against a customer's
 * production table, and that call has to be testable without a network. The hook in
 * `api/bulkAnalysis.ts` does the requesting and holds none of these rules.
 *
 * **One plan per action, not one plan covering both.** Run Profile and Classify PII are separate
 * controls with separate confirmations and separate progress, so a plan carrying both would be a
 * plan every caller had to filter — and the two skip rules read from different places, which is
 * exactly the sort of thing that gets filtered on the wrong one.
 *
 * **The "already done" answers come from the backend, never from local state.** A profile is done
 * when `DatasetFacts.last_profiled_at` is set; a classification is done when `CatalogRow.classified`
 * is true. Both are persisted and both are already read by this screen for its columns — so a reload
 * cannot lose the record of what ran, and re-opening the page cannot re-trigger completed work. A
 * frontend "we did this" flag would be a second answer to a question the server already answers, and
 * the one that disagrees after a refresh.
 */

export type AnalysisKind = 'profile' | 'classify';

/** One unit of work: a dataset and which action it needs. */
export interface AnalysisTask {
  readonly datasetId: string;
  readonly kind: AnalysisKind;
}

export interface BulkPlan {
  readonly kind: AnalysisKind;
  readonly tasks: readonly AnalysisTask[];
  /** Datasets the server says have already had this action, so none was planned for them. */
  readonly skipped: number;
  /** Datasets considered, whether or not they produced a task. */
  readonly considered: number;
  /**
   * True when the status this plan skips on could not be read at all.
   *
   * Reported rather than folded into `skipped`, because the two mean opposite things and a reader
   * has to be able to tell them apart: `skipped` says the work is done, this says we do not know.
   * Without it a failed facts read would produce an empty plan and a dialog reading "0 profiles"
   * for an estate that has never been profiled — which reads as "all done" and is the inverse of
   * the truth.
   */
  readonly unknownStatus: boolean;
}

/** What one bulk action would do, given what the server says has already happened.
 *
 * **Facts absent is not "never profiled" here, and that asymmetry with `profileReading` is
 * deliberate.** That function renders a column and must show *something* for a dataset whose facts
 * are missing, so it reads `undefined` as never-profiled. This decides whether to spend twenty
 * scans of a customer's table, and an unread fact is not evidence that the work is needed — so a
 * failed facts read plans nothing and says so, rather than planning everything on a guess. That is
 * the safe direction for a control whose mistake is measured in full table scans.
 *
 * `vanished` datasets are excluded from both actions: the object is gone from the source, so either
 * would dial a customer's database for a table that is not there. That is the "dataset removed"
 * case arriving before the run starts — see the runner for the one that arrives during it.
 */
export function planBulkAnalysis(
  rows: readonly CatalogRow[],
  facts: ReadonlyMap<string, DatasetFacts> | undefined,
  kind: AnalysisKind,
): BulkPlan {
  const tasks: AnalysisTask[] = [];
  let skipped = 0;
  let considered = 0;
  /* Only the profile half depends on the facts read. A classification's status rides on the
     catalogue row, which the listing already returned — so a facts failure must not stop it. */
  const unknownStatus = kind === 'profile' && facts === undefined;

  for (const row of rows) {
    if (row.state === 'vanished') continue;
    considered += 1;
    if (unknownStatus) continue;

    const done =
      kind === 'profile' ? facts?.get(row.id)?.last_profiled_at != null : row.classified;
    if (done) {
      skipped += 1;
    } else {
      tasks.push({ datasetId: row.id, kind });
    }
  }

  return { kind, tasks, skipped, considered, unknownStatus };
}

export type TaskOutcome = 'done' | 'failed';

export interface BulkProgress {
  readonly total: number;
  readonly done: number;
  readonly failed: number;
  /** Tasks neither finished nor failed yet. Never negative. */
  readonly outstanding: number;
  readonly finished: boolean;
}

/** Progress from a total and the outcomes recorded so far.
 *
 * Derived rather than incremented, so the three counts cannot drift out of agreement with each
 * other — an incrementing counter that missed one `finally` would report a run as unfinished for
 * ever, with the button stuck in its loading state and nothing to press.
 */
export function bulkProgress(
  total: number,
  outcomes: readonly TaskOutcome[],
): BulkProgress {
  const done = outcomes.filter((outcome) => outcome === 'done').length;
  const failed = outcomes.length - done;
  return {
    total,
    done,
    failed,
    outstanding: Math.max(0, total - outcomes.length),
    finished: outcomes.length >= total,
  };
}

/** How many requests a bulk run has in flight at once.
 *
 * **Four, and the number is a bound on a customer's database rather than on this browser.** Each
 * profile is roughly 20 full `GROUP BY` scans at the source and CLAUDE.md §9 records that nothing
 * server-side bounds concurrent profiles — no `SourceLoadGate` admission is taken on that path. So
 * the only thing standing between "run for all datasets" and a hundred simultaneous scans of a
 * production database is this constant. Four keeps the run useful on a 32-table source while
 * leaving the source able to serve its own traffic.
 *
 * Not configurable, deliberately: a control that let a reader raise it would be a control that lets
 * a reader overload their own database from a screen.
 */
export const BULK_CONCURRENCY = 4;

/** The largest number of datasets one bulk run will touch.
 *
 * A bound, and a *reported* one — `BulkPlan.considered` against this is what lets the confirmation
 * say the run covers part of the estate rather than implying it covers all of it. Two thousand
 * datasets is already up to forty thousand table scans; past that the honest answer is that this
 * belongs in `lumiq-cli` against a schedule rather than behind a button.
 */
export const MAX_BULK_DATASETS = 2000;
